import { useAuthorizationServiceV1 } from "@rw1/api-services";
import { Authorization_V1 } from "@rw1/auto-generated-clients";
import { useMemo } from "react";
import { useFetchAll } from "./useFetchAll";

export const ALL_ROLES_QUERY_KEY = "allRoles";

export interface UseAllRolesOptions {
  pageSize?: number;
  enabled?: boolean;
}

export interface UseAllRolesResult {
  allRoles: Authorization_V1.Role[];
  rolesMap: Map<string, Authorization_V1.Role>;
  isLoading: boolean;
  error: Error | null;
}

export const useAllRoles = (
  options: UseAllRolesOptions = {},
): UseAllRolesResult => {
  const { pageSize = 1000, enabled = true } = options;

  const authorizationServiceV1 = useAuthorizationServiceV1();

  const {
    data: rawRoles,
    isLoading,
    error,
  } = useFetchAll<Authorization_V1.Role>(
    [ALL_ROLES_QUERY_KEY, pageSize],
    async (page, size) => {
      return await authorizationServiceV1.getRoles(
        page,
        size,
        undefined,
        undefined,
        undefined,
      );
    },
    { enabled, pageSize },
  );

  const allRoles = useMemo<Authorization_V1.Role[]>(() => {
    return rawRoles ?? [];
  }, [rawRoles]);

  const rolesMap = useMemo(() => {
    const map = new Map<string, Authorization_V1.Role>();
    for (const role of allRoles) {
      if (role.roleId) {
        map.set(role.roleId, role);
      }
    }

    return map;
  }, [allRoles]);

  return useMemo(
    () => ({
      allRoles,
      rolesMap,
      isLoading,
      error,
    }),
    [allRoles, rolesMap, isLoading, error],
  );
};
